import React, { useState, useEffect } from 'react';
import { useNotification } from '../../../context/NotificationContext';
import { retailerAPI } from '../../../utils/api';

const RetailerOrders = () => {
  const { showSuccess, showError } = useNotification();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, processing, completed, cancelled

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const res = await retailerAPI.getOrders();
      if (res.data?.success) {
        let filtered = res.data.data || [];
        if (filter !== 'all') {
          filtered = filtered.filter(order => order.status === filter);
        }
        setOrders(filtered);
      } else {
        showError(res.data?.message || 'Failed to fetch orders');
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      showError('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const response = await fetch(`/api/retailer/order/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      const data = await response.json();
      if (data.success) {
        showSuccess('Order status updated successfully');
        fetchOrders();
      } else {
        showError(data.message || 'Failed to update order status');
      }
    } catch (error) {
      showError('Failed to update order status');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div>
        <p className="text-gray-400 mt-2">Loading orders...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gold">My Orders</h1>

      {/* Filter Tabs */}
      <div className="flex space-x-2 bg-gray-800 rounded-lg p-1 overflow-x-auto">
        {['all', 'pending', 'processing', 'completed', 'cancelled'].map((tab) => (
          <button
            key={tab}
            onClick={() => setFilter(tab)}
            className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-colors ${filter === tab
              ? 'bg-gold text-dark'
              : 'text-gray-400 hover:text-light'
              }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Orders List */}
      <div className="space-y-4">
        {orders.length === 0 ? (
          <div className="text-center py-12 bg-gray-800 rounded-xl">
            <p className="text-gray-400">No orders found</p>
          </div>
        ) : (
          orders.map((order) => (
            <div
              key={order.id || order._id}
              className="bg-gray-800 rounded-xl p-6"
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-light">Order #{order.id || order.order_number}</h3>
                  <p className="text-sm text-gray-400">
                    {new Date(order.created_at || order.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${order.status === 'pending' ? 'bg-yellow-900 text-yellow-300' :
                  order.status === 'processing' ? 'bg-blue-900 text-blue-300' :
                    order.status === 'completed' ? 'bg-green-900 text-green-300' :
                      'bg-red-900 text-red-300'
                  }`}>
                  {order.status}
                </span>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex justify-between">
                  <span className="text-gray-400">Customer:</span>
                  <span className="text-light">{order.customer?.name || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Items:</span>
                  <span className="text-light">{order.items?.length || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Total Amount:</span>
                  <span className="text-gold font-medium">₹{order.total_amount || 0}</span>
                </div>
              </div>

              {order.items && order.items.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm text-gray-400 mb-2">Items:</p>
                  <div className="space-y-1">
                    {order.items.map((item, index) => (
                      <div key={index} className="flex justify-between text-sm">
                        <span className="text-light">{item.name} x {item.quantity}</span>
                        <span className="text-gray-400">₹{item.price * item.quantity}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {order.status === 'pending' && (
                <div className="flex space-x-2">
                  <button
                    onClick={() => updateOrderStatus(order.id || order._id, 'processing')}
                    className="flex-1 bg-gold text-dark py-2 rounded-lg font-medium"
                  >
                    Accept Order
                  </button>
                  <button
                    onClick={() => updateOrderStatus(order.id || order._id, 'cancelled')}
                    className="flex-1 bg-red-900 text-red-300 py-2 rounded-lg font-medium"
                  >
                    Cancel
                  </button>
                </div>
              )}

              {order.status === 'processing' && (
                <div className="flex space-x-2">
                  <button
                    onClick={() => updateOrderStatus(order.id || order._id, 'completed')}
                    className="flex-1 bg-green-600 text-white py-2 rounded-lg font-medium"
                  >
                    Mark as Completed
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default RetailerOrders;
